import type { VerseRange } from "./plan";

// 中文（简/繁）保持计划数据里的原始写法（含中文数字），其余语言按所选版本本地化书卷名生成。
export const isChineseLang = (lang: string): boolean => /^zh\b/.test(lang || "");

const CN_DIGITS = ["〇", "一", "二", "三", "四", "五", "六", "七", "八", "九"];

// 逐位中文数字：132 → 一三二，8 → 八
const cnNum = (n: number): string =>
  String(n)
    .split("")
    .map((c) => CN_DIGITS[Number(c)])
    .join("");

export const formatChapterNumber = (n: number, lang: string): string =>
  isChineseLang(lang) ? cnNum(n) : String(n);

const isWholeRange = (r: VerseRange): boolean => r.verseStart === 1 && r.verseEnd >= 999;

/**
 * 把结构化的经节范围拼成可读引用，例如：
 *   en: "Psalms 132–134", "Matthew 1:1–25", "Genesis 1:1–2:25", "1 Kings 22:51–2 Kings 2:18"
 *   zh: "诗一三二～一三四", "太一1～25", "创一1～二25"（zh 分支保留，前端中文仍走原始 raw）
 */
export function formatReadingReference(
  ranges: VerseRange[],
  resolveBookName: (bookId: string) => string,
  lang: string
): string {
  if (!ranges.length) return "";
  const zh = isChineseLang(lang);
  const sep = zh ? "～" : "–";
  const n = (v: number): string => (zh ? cnNum(v) : String(v));
  const name = (bookId: string): string => resolveBookName(bookId) || bookId;

  // 全部为整章（如诗一三二~一三四）：书卷 + 起始章，跨章时带结束章
  if (ranges.every(isWholeRange)) {
    const start = ranges[0];
    const end = ranges[ranges.length - 1];
    if (start === end) return zh ? `${name(start.book)}${n(start.chapter)}` : `${name(start.book)} ${start.chapter}`;
    return zh
      ? `${name(start.book)}${n(start.chapter)}${sep}${n(end.chapter)}`
      : `${name(start.book)} ${start.chapter}${sep}${end.chapter}`;
  }

  // 含起止节号：整个部分视为一个跨度（创一1～二25 / Genesis 1:1–2:25）
  const start = ranges[0];
  const end = ranges[ranges.length - 1];
  const sameBook = start.book === end.book;
  const sameChapter = start.chapter === end.chapter;
  // 半节标记（上/下）仅用于中文写法，其他语言不区分半节
  const halfStart = zh ? start.halfStart || "" : "";
  const halfEnd = zh ? end.halfEnd || "" : "";

  const startRef = zh
    ? `${name(start.book)}${n(start.chapter)}${n(start.verseStart)}${halfStart}`
    : `${name(start.book)} ${start.chapter}:${start.verseStart}${halfStart}`;

  const endChapter = sameChapter ? "" : `${n(end.chapter)}${zh ? "" : ":"}`;
  const endVerse = end.verseEnd >= 999 ? "" : `${n(end.verseEnd)}${halfEnd}`;
  const endRef = zh
    ? `${sameBook ? "" : name(end.book)}${endChapter}${endVerse}`
    : `${sameBook ? "" : name(end.book) + " "}${endChapter}${endVerse}`;

  if (!endRef) return startRef;
  return `${startRef}${sep}${endRef}`;
}
